"use client";

import React, { useState } from "react";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import { Phone, Copy, Mail, ChevronUp, ChevronDown } from "lucide-react";
import { ContactCard } from "./ContactCard";
import { useClipboard } from "../../../hooks/useClipboard";
import { ContactForm } from "./ContactForm";
import { useTranslations, useLocale } from "next-intl";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import { isEmailAddress, isPhoneNumber } from "@/app/utils/validation/contact";
import type { LocalizedText } from "@/app/api/utils/types/tenants";

function getLocalizedText(text: LocalizedText, locale: string): string {
  return text[locale as keyof LocalizedText] || text.en || "";
}

export default function Contact() {
  const t = useTranslations();
  const locale = useLocale();
  const {
    name: tenantName,
    contactPhone,
    contactEmail,
    faqs: tenantFaqs,
  } = useTenantBranding();
  const phone = contactPhone?.trim() ?? "";
  const email = contactEmail?.trim() ?? "";
  const hasPhone = isPhoneNumber(phone);
  const hasEmail = isEmailAddress(email);

  const faqs =
    tenantFaqs && tenantFaqs.length > 0
      ? tenantFaqs.map((faq) => ({
          question: getLocalizedText(faq.question, locale),
          answer: getLocalizedText(faq.answer, locale),
        }))
      : [
          {
            question: t("Other.whyChooseGate"),
            answer: t("Other.whyChooseGateDescription"),
          },
          {
            question: t("Other.whatIsGatePay"),
            answer: t("Other.whatIsGatePayDescription"),
          },
          {
            question: t("Other.whyUseGateTravel", { tenantName }),
            answer: t("Other.whyUseGateTravelDescription", { tenantName }),
          },
          {
            question: t("Other.canGetCustomerSupport"),
            answer: t("Other.canGetCustomerSupportDescription"),
          },
        ];
  const { copiedId, copy } = useClipboard();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-sleap-gray-950">
      <Header />

      <main className="flex-grow py-4 sm:py-4 md:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-4 md:px-6">
          {/* Contact Header */}
          <div className="self-stretch flex flex-col justify-start gap-6 w-full">
            <h1 className="self-stretch text-3xl font-bold font-switzer leading-loose text-zinc-900 dark:text-white sm:text-2xl lg:text-4xl">
              {t("Other.contact")}
            </h1>
          </div>

          {/* Support Service Line */}
          {hasPhone && (
            <ContactCard title={t("Other.supportServiceLine")}>
              <div className="self-stretch flex flex-col gap-3 w-full">
                <p className="self-stretch text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
                  {t("Other.supportTeamAvailable24per7")}
                </p>
                <div className="self-stretch h-12 px-4 py-2 bg-neutral-100 rounded-xl outline outline-1 outline-neutral-100 inline-flex justify-start items-center gap-2 w-full">
                  <div className="w-5 h-5 relative">
                    <Phone
                      size={20}
                      className="text-zinc-900 dark:text-white"
                    />
                  </div>
                  <p className="flex-1 text-base font-semibold font-switzer leading-tight text-zinc-900 mb-0">
                    {phone}
                  </p>
                  <button
                    onClick={() => copy(phone, "phone")}
                    className="p-1 hover:text-sleap-purple-300 transition-colors duration-200"
                    aria-label={t("Other.copyPhoneNumber")}
                  >
                    {copiedId === "phone" ? (
                      <span className="text-xs text-bold font-switzer text-green-600">
                        {t("Other.copiedToClipboard")}
                      </span>
                    ) : (
                      <div className="w-5 h-5 relative">
                        <Copy
                          size={20}
                          className="text-zinc-900 dark:text-white"
                        />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </ContactCard>
          )}

          {/* Support Email Address */}
          {hasEmail && (
            <>
              {hasPhone && (
                <div className="self-stretch h-px bg-zinc-200 lg:hidden" />
              )}
              <ContactCard title={t("Other.supportEmailAddress")}>
                <div className="self-stretch flex flex-col gap-3 w-full">
                  <p className="self-stretch text-base font-normal font-switzer leading-normal text-zinc-900 dark:text-sleap-gray-200">
                    {t("Other.weWillGetBackToYouAssap")}
                  </p>
                  <div className="self-stretch h-12 px-4 py-2 bg-neutral-100 rounded-xl outline outline-1 outline-neutral-100 inline-flex justify-start items-center gap-2 w-full">
                    <div className="w-5 h-5 relative">
                      <Mail
                        size={20}
                        className="text-zinc-900 dark:text-white"
                      />
                    </div>
                    <p className="flex-1 text-base font-semibold font-switzer leading-tight text-zinc-900 sm:text-sm mb-0">
                      {email}
                    </p>
                    <button
                      onClick={() => copy(email, "email")}
                      className="p-1 hover:text-sleap-purple-300 transition-colors duration-200"
                      aria-label={t("Other.copyEmailAddress")}
                    >
                      {copiedId === "email" ? (
                        <span className="text-xs text-bold font-switzer text-green-600">
                          {t("Other.copiedToClipboard")}
                        </span>
                      ) : (
                        <div className="w-5 h-5 relative">
                          <Copy
                            size={20}
                            className="text-zinc-900 dark:text-white"
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </ContactCard>
            </>
          )}

          {/* Contact Form */}
          {(hasPhone || hasEmail) && (
            <div className="self-stretch h-px bg-zinc-200 lg:hidden" />
          )}

          <ContactForm />

          {/* FAQs */}
          <div className="self-stretch h-px bg-zinc-200 lg:hidden" />
          <div className="self-stretch py-4 flex flex-col justify-start items-start gap-4 w-full lg:pt-10 lg:pb-24">
            <h2 className="self-stretch text-3xl font-bold font-switzer leading-loose text-zinc-900 dark:text-white sm:text-2xl lg:text-4xl">
              {t("HomePage.FAQs")}
            </h2>
            <div className="self-stretch flex flex-col justify-start gap-2 w-full">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="lg:border-b lg:border-sleap-gray-400"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="self-stretch h-12 py-2 bg-white dark:bg-sleap-gray-950 inline-flex justify-between items-center gap-2 w-full pl-0 lg:py-4 lg:h-auto"
                  >
                    <span className="flex-1 text-left text-base font-semibold font-switzer leading-tight text-black dark:text-white lg:text-2xl lg:font-medium">
                      {faq.question}
                    </span>
                    <div className="w-5 h-5 relative">
                      {expandedFAQ === index ? (
                        <ChevronUp
                          size={20}
                          className="text-black dark:text-white"
                        />
                      ) : (
                        <ChevronDown
                          size={20}
                          className="text-black dark:text-white"
                        />
                      )}
                    </div>
                  </button>
                  {expandedFAQ === index && (
                    <p className="self-stretch text-sm font-normal font-switzer leading-tight text-zinc-900 dark:text-sleap-gray-200 w-full pl-0 lg:text-lg">
                      {faq.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
